import { useEffect, useState } from "react";
import { createPartnerSubbie } from "../utils/api";

/* ============================================================================
 * AddSubbieModal — nested above SubcontractorComplete (zIndex 1200)
 * ----------------------------------------------------------------------------
 * POSTs a new record to the partner Subbie table with SPID = this cleaner
 * (Airtable displays the link as the cleaner's CleanerID / Name), then links
 * it on the cleaner's Subbie field on the final table.
 *   • Subbie  – entered by the user (pre-filled with whatever they typed)
 *   • Name / Email / Phone / Surname – pre-filled from the cleaner, editable
 *
 * Props:
 *   cleaner       – the active final-subbie record
 *   initialSubbie – text typed into the autocomplete (pre-fills Subbie)
 *   onCreated     – (data) => void   data = { created: {id,name}, ...refreshed record }
 *   onClose       – () => void
 * ==========================================================================*/

// (042) 409-0020 — same rule as UsersManagement's mobile field
const formatPhone = (v) => {
  const d = (v ?? "").toString().replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const E = {
  overlay: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(6px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 1200, padding: 20,
  },
  modal: {
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    width: "100%", maxWidth: 560, overflow: "hidden",
    boxShadow: "0 25px 80px rgba(0,0,0,0.6)",
  },
  gradientBar: { height: 3, background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)" },
  header: { padding: "20px 24px 8px", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  headTitle: { fontSize: "1.05rem", fontWeight: 700, color: "#f1f5f9", margin: 0 },
  headSub: { fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", marginTop: 4 },
  body: { padding: "16px 24px 8px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", columnGap: 14, rowGap: 10 },
  footer: {
    padding: "16px 24px", display: "flex", justifyContent: "flex-end", gap: 10,
    background: "rgba(0,0,0,0.2)", borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  cancelBtn: {
    background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10,
    padding: "10px 18px", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
  },
  confirmBtn: (disabled) => ({
    background: "linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)", color: "#0f172a",
    border: "none", borderRadius: 10, padding: "10px 22px", fontSize: "0.85rem",
    fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1, boxShadow: "0 4px 14px rgba(34,211,238,0.3)",
  }),
  error: {
    margin: "0 24px", padding: "8px 12px", borderRadius: 8,
    background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
    color: "#fca5a5", fontSize: "0.8rem",
  },
};

const FIELDS = [
  { key: "subbie", label: "Subbie", span: 2, required: true },
  { key: "name", label: "Name" },
  { key: "surname", label: "Surname" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", placeholder: "(042) 409-0020", maxLength: 14 },
];

const AddSubbieModal = ({ cleaner, initialSubbie = "", onCreated, onClose }) => {
  const first = (cleaner?.firstName ?? "").toString().trim();
  const surname = (cleaner?.surname ?? "").toString().trim();
  const mobile = (cleaner?.mobile ?? "").toString().trim();

  const [form, setForm] = useState({
    subbie: initialSubbie.trim(),
    name: first,
    surname,
    email: (cleaner?.email ?? "").toString().trim(),
    phone: formatPhone(mobile),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [saving, onClose]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === "phone" ? formatPhone(e.target.value) : e.target.value }));

  const canSave = form.subbie.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const res = await createPartnerSubbie(cleaner.recordId, {
        subbie: form.subbie.trim(),
        name: form.name.trim(),
        surname: form.surname.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      if (res.status !== "success") throw new Error(res.error || "Could not create subbie");
      onCreated(res.data);
    } catch (err) {
      setError(err.message || "Could not create subbie");
      setSaving(false);
    }
  };

  return (
    <div
      style={E.overlay}
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div style={E.modal}>
        <div style={E.gradientBar} />
        <div style={E.header}>
          <h5 style={E.headTitle}>Add New Subbie</h5>
          <div style={E.headSub}>
            Enter the Subbie name. The record is created with{" "}
            <strong style={{ color: "#f1f5f9" }}>{cleaner?.name}</strong> linked in SPID.
          </div>
        </div>

        <div style={E.body}>
          {FIELDS.map((f) => (
            <div
              key={f.key}
              className="ob-field"
              style={{ minWidth: 0, gridColumn: f.span === 2 ? "1 / -1" : undefined }}
            >
              <label className="ob-label">
                {f.label}
                {f.required && <span style={{ color: "#f87171" }}> *</span>}
              </label>
              <input
                type={f.type || "text"}
                className="ob-input"
                value={form[f.key]}
                onChange={set(f.key)}
                disabled={saving}
                autoFocus={f.key === "subbie"}
                placeholder={f.key === "subbie" ? "e.g. JN Bolo or SNL" : f.placeholder || ""}
                maxLength={f.maxLength}
              />
            </div>
          ))}
        </div>

        {error && <div style={E.error}>{error}</div>}

        <div style={E.footer}>
          <button type="button" style={E.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" style={E.confirmBtn(!canSave)} onClick={handleSave} disabled={!canSave}>
            {saving ? "Saving…" : "Create & link"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSubbieModal;